import { Button, Checkbox, Divider, Tooltip } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import {
  DropDownDBFeatureInstance,
  DropdownEntityOption,
  TEditingEntity,
  TEntityLink,
  TLinkSource
} from "components/feature-entity-linking-page/models";
import {
  getTLinkSourceMaterialObject,
  translateFeatureToOptions
} from "components/feature-entity-linking-page/utils";
import { FeatureTypes } from "entity-app/constants";
import { useCallback, useMemo, useState } from "react";
import BlueTickIcon from "components/svg-icons/blue-tick-icon";
import { InfoCircleOutlined } from "@ant-design/icons";
import PredecessorSuccessorTag from "../cards/material-predecessor-successor-tag";
import VirtualList from "../virtual-list";

export default function MaterialListing({
  materials,
  editingEntity,
  onUpdateEditingEntityList,
  selectedEntityInfo
}: {
  materials: Array<DropDownDBFeatureInstance>;
  editingEntity: TEditingEntity;
  onUpdateEditingEntityList: (props: { updatedlist: TLinkSource[] }) => void;
  selectedEntityInfo: TEntityLink | undefined;
}) {
  const [searchText, setSearchText] = useState<string>("");
  const [isSuggested, setSuggested] = useState(false);

  const specNumber = editingEntity?.entityDetail?.specSection?.section_number;
  const allEntities = useMemo(() => {
    // editingEntity is a required non-nullable prop and is dereferenced unguarded elsewhere (e.g. editingEntity.list), so it is always truthy here
    /* v8 ignore next */
    if (!editingEntity) return [];
    return translateFeatureToOptions(materials, specNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, specNumber]);

  const entities = useMemo(() => {
    let filtered = allEntities;
    if (searchText) {
      filtered = filtered.filter((x) => x.label.includes(searchText));
    }
    if (isSuggested) {
      filtered = filtered.filter((x) => x.isSuggested);
    }
    return filtered;
  }, [allEntities, searchText, isSuggested]);

  const selectedIdSet = useMemo(() => {
    const set = new Set<string>();
    editingEntity.list.forEach((x) => {
      if (x.feature_instance?.id) set.add(x.feature_instance.id);
    });
    return set;
  }, [editingEntity.list]);

  const isAllSuggestedSelected = useMemo(() => {
    const suggestedEntities = allEntities.filter((x) => x.isSuggested);
    if (suggestedEntities.length === 0) return false;
    return suggestedEntities.every((x) => selectedIdSet.has(x.id));
  }, [allEntities, selectedIdSet]);

  const onChangeCheckbox = useCallback(
    (checked: boolean, item: DropdownEntityOption) => {
      if (checked) {
        const sampleObject = getTLinkSourceMaterialObject(item);
        onUpdateEditingEntityList({
          updatedlist: [...editingEntity.list, sampleObject]
        });
      } else {
        onUpdateEditingEntityList({
          updatedlist: editingEntity.list.filter(
            (x) => x.featureInstanceId !== item.id
          )
        });
      }
    },
    [editingEntity.list, onUpdateEditingEntityList]
  );

  // CiqAgSearch debounces (500ms) before it calls back, so this no longer
  // keeps a timer of its own.
  const onSearchChange = useCallback((value: string) => {
    setSearchText((value || "").toLowerCase().trim());
  }, []);

  const linkingAsPredecessors =
    editingEntity.materialLinkDirection === "predecessor";

  const isBlockedByMaterialRole = (val: DropdownEntityOption) => {
    if (selectedEntityInfo?.feature_type_id !== FeatureTypes.MATERIALS) {
      return false;
    }
    if (linkingAsPredecessors) {
      return (
        (val.instance?.predecessor_material_links?.aggregate?.count ?? 0) > 0
      );
    }
    return (val.instance?.successor_material_links?.aggregate?.count ?? 0) > 0;
  };

  const blockedMaterialTag = linkingAsPredecessors
    ? "Successor for another Material"
    : "Predecessor for another Material";

  const blockedMaterialTooltip = linkingAsPredecessors
    ? "Cannot be linked as this Material is a Successor to other Materials."
    : "Cannot be linked as this Material is a Predecessor to other Materials.";

  const linkDirectionLabel =
    editingEntity.materialLinkDirection === "predecessor"
      ? "Predecessors"
      : "Successors";

  const btnCss = useMemo(() => {
    return isSuggested
      ? "text-ciq-blue-700 border-ciq-blue-700 focus:text-ciq-blue-700 focus:border-ciq-blue-700"
      : "text-black text-opacity-90 border-ciq-gray-300 focus:text-black text-opacity-90 focus:border-ciq-gray-300";
  }, [isSuggested]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden features-card">
      <div className="flex items-center justify-between px-4">
        <div
          className="flex font-bold py-2"
          data-testid="materiallisting-title"
        >
          List of Materials
        </div>
        <div
          className="flex items-center gap-2 ml-auto text-ciq-black-a87"
          data-testid="materiallisting-info-label"
        >
          <InfoCircleOutlined className="!text-ciq-black-a87 relative -top-px" />
          <span>Selected materials will be added as {linkDirectionLabel}</span>
        </div>
        <div className="flex space-x-1">
          {isSuggested && (
            <Checkbox
              checked={isAllSuggestedSelected}
              data-testid="materiallisting-checkbox-all"
              onChange={(e) => {
                // setAllSuggestedSelected(e.target.checked);
                if (e.target.checked) {
                  const suggestedObjects = allEntities
                    .filter((x) => x.isSuggested)
                    .map((item) => getTLinkSourceMaterialObject(item));
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: [
                      ...nonSuggestedLinkedObjects,
                      ...suggestedObjects
                    ]
                  });
                } else {
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: nonSuggestedLinkedObjects
                  });
                }
              }}
            >
              {isAllSuggestedSelected ? "Unlink All" : "Link All"}
            </Checkbox>
          )}
        </div>
      </div>
      {isSuggested && (
        <div
          className="material-linking--suggestedLblDiv"
          data-testid="materiallisting-suggested-label"
        >
          <span className="material-linking--suggestInfoLbl ml-1">
            Suggested materials have the same spec section as the selected
            submittal.
          </span>
        </div>
      )}
      <div className="flex w-full pb-4 px-4">
        <CiqAgSearch
          placeholder="Search Material Name or ID"
          className="w-full pr-[10px]"
          onSearch={onSearchChange}
          data-testid="materiallisting-search-material-name-id"
        />

        <Divider orientation="vertical" className="h-7 m-0 mr-4 pt-[35px]" />
        <Button
          icon={isSuggested ? <BlueTickIcon /> : null}
          className={`${btnCss} flex justify-center items-center  rounded p-1 gap-x-1 px-3`}
          onClick={() => setSuggested(!isSuggested)}
          data-testid="materiallisting-button-filter-suggestions"
        >
          <span>Filter Suggestions</span>
        </Button>
      </div>
      <VirtualList
        // entities comes from a useMemo that always returns an array, so the || [] fallback is never taken
        /* v8 ignore next */
        items={entities || []}
        itemHeight={46}
        className="w-full flex-1 min-h-0"
        renderItem={(item) => (
          <div className="flex justify-between items-center h-[45px] px-4 border-0 border-b border-solid border-ciq-gray-950 border-opacity-20">
            <Checkbox
              disabled={isBlockedByMaterialRole(item)}
              checked={selectedIdSet.has(item.id)}
              onChange={(e) => {
                onChangeCheckbox(e.target.checked, item);
              }}
              data-testid="materiallisting-checkbox-item"
            >
              <Tooltip
                data-testid="materiallisting-tooltip-item"
                title={
                  isBlockedByMaterialRole(item) ? blockedMaterialTooltip : ""
                }
              >
                {/* Single child: antd v6 drops the Tooltip's own className and
                      wraps multiple children in a bare inline <span>, so the
                      <i> (inline) and the truncate <div> (block) fell onto two
                      lines instead of one flex row. Keep the flex container on an
                      explicit wrapper (matches submittal-listing). */}
                <div className="flex items-center space-x-2">
                  <i>{item.pre}</i>
                  <div className="truncate">{item.title}</div>
                </div>
              </Tooltip>
            </Checkbox>
            {isBlockedByMaterialRole(item) && (
              <PredecessorSuccessorTag
                title={blockedMaterialTag}
                showIcon={false}
                data-testid="materiallisting-predecessor-successor-tag"
              />
            )}
          </div>
        )}
      />
    </div>
  );
}
